<!--
/**
 * @copyright   OnlyOneProd © 2025
 * @authors     Jonathan COULLET <contact@onlyoneprod.com>
 * @link        https://onlyoneprod.com
 * @notice      All information contained herein is,
 *              and remains the property of OnlyOneProd and its suppliers,
 *              Any dissemination of this information or reproduction
 *              of this material is strictly forbidden unless prior
 *              written permission is obtained from OnlyOneProd.
 */
-->

<template>
    <section id="services" class="screen">
        <CommonShapeAnimation shape="polygon" :sides="4" :rotation="-20" position="left" />
        <div class="content">
            <div class="wrapper">
                <div class="intro">
                    <h2>Nos services</h2>
                    <p>De l'identité visuelle au digital, en passant par des supports créatifs et des productions
                        audiovisuelles
                        de qualité, nous construisons une communication qui vous ressemble.</p>
                    <a id="services-header-action" @click="clickHandler('header', 'contact')" title="Contactez-nous">
                        <CommonButton size="large" icon="next">Je me lance</CommonButton>
                    </a>
                </div>
                <div class="grid-3">
                    <article class="panel">
                        <header>
                            <IconIdentity />
                            <h3>Identité visuelle</h3>
                        </header>
                        <p><strong>Concevez l’essence de votre marque.</strong></p>
                        <p class="grow">Nous créons des logos et chartes graphiques qui racontent votre histoire et
                            font forte
                            impression. Construisez une identité qui capte l’attention et inspire la confiance.</p>
                        <a id="services-identity" @click="clickHandler('identity', 'contact')" title="Contactez-nous">
                            <CommonButton icon="next">Je me lance</CommonButton>
                        </a>
                    </article>
                    <article class="panel">
                        <header>
                            <IconSupport />
                            <h3>Supports de communication</h3>
                        </header>
                        <p><strong>Laissez une trace mémorable.</strong></p>
                        <p class="grow">Cartes de visite élégantes, flyers percutants, Devantures qui attirent ou
                            goodies
                            personnalisés… Nous donnons vie à des supports créatifs qui parlent à vos clients et
                            valorisent votre marque.</p>
                        <a id="services-supports" @click="clickHandler('supports', 'contact')" title="Contactez-nous">
                            <CommonButton icon="next">Je me lance</CommonButton>
                        </a>
                    </article>
                    <article class="panel">
                        <header>
                            <IconWeb />
                            <h3>Web</h3>
                        </header>
                        <p><strong>Faites vibrer le digital.</strong></p>
                        <p class="grow">Vous avez besoin d’un site vitrine, une plateforme de réservation, e-commerce ou
                            même une
                            application métier complète ? Nous développons des solutions modernes et performantes,
                            pensées pour vos besoins.</p>
                        <a id="services-web" @click="clickHandler('web', 'contact')" title="Contactez-nous">
                            <CommonButton icon="next">Je me lance</CommonButton>
                        </a>
                    </article>
                    <article class="panel">
                        <header>
                            <IconApp />
                            <h3>Application mobile</h3>
                        </header>
                        <p><strong>Une expérience utilisateur à portée de main.</strong></p>
                        <p class="grow">Nous imaginons et développons des applications mobiles intuitives, prêtes à
                            engager vos
                            utilisateurs et à renforcer votre présence.</p>
                        <a id="services-app" @click="clickHandler('app', 'contact')" title="Contactez-nous">
                            <CommonButton icon="next">Je me lance</CommonButton>
                        </a>
                    </article>
                    <article class="panel">
                        <header>
                            <IconWeb />
                            <h3>Photo &amp; vidéo</h3>
                        </header>
                        <p><strong>Racontez votre histoire en images.</strong></p>
                        <p class="grow">Produits, événements, vidéos institutionnelles… Nous capturons l’essence de
                            votre activité
                            avec des visuels professionnels qui marquent les esprits.</p>
                        <a id="services-photo-video" @click="clickHandler('photo-video', 'contact')" title="Contactez-nous">
                            <CommonButton icon="next">Je me lance</CommonButton>
                        </a>
                    </article>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import CommonShapeAnimation from "@/components/common/CommonShapeAnimation.vue";
import CommonButton from "@/components/common/CommonButton.vue";

import IconApp from "@/components/icons/IconApp.vue";
import IconIdentity from "@/components/icons/IconIdentity.vue";
import IconSupport from "@/components/icons/IconSupport.vue";
import IconWeb from "@/components/icons/IconWeb.vue";

import { trackButtonClick } from "@/helpers/analytics.helper";
import { scrollToSection } from "@/helpers/scroll.helper";

const clickHandler = (origin: string, destination: string) => {
    trackButtonClick(`services-${origin}-${destination}`);
    scrollToSection(destination);
}
</script>

<style lang="sass">
</style>